// @flow

import * as React from 'react';
import classNames from 'classnames';
import Badge from 'sail/Badge';
import Box from 'sail/Box';
import Icon from 'sail/Icon';
import {Text} from 'sail/Text';
import {InlineLink} from 'sail/InlineLink';
import {Heading} from 'docs/components';
import {useLocation} from 'docs/data';
import {useExpandableState} from 'docs/hooks';
import './Section.scss';
import {defineMessages, FormattedMessage} from 'react-intl';

const messages = defineMessages({
  optional: {
    id: 'docsApp.components.section.optional',
    description: 'Label on a section that is optional',
    defaultMessage: 'Optional',
  },
});

type SectionBadgeListProps = {|
  badges: Array<string>,
|};

const SectionBadgeList = ({badges}: SectionBadgeListProps) => (
  <Box padding={{left: 8}} className="Section-badge-list">
    {badges.map((badge, index) => (
      <Badge key={index} color="gray" label={badge} className="Section-badge" />
    ))}
  </Box>
);

type SectionExpandIconProps = {|
  expanded: boolean,
|};

const SectionExpandIcon = ({expanded}: SectionExpandIconProps) => (
  <Icon
    color="gray"
    size={14}
    icon={expanded ? 'chevronUp' : 'chevronDown'}
    className="Section-expand-icon"
  />
);

type SectionProps = {|
  id: string,
  badges?: Array<string>,
  children?: React.Node,
  className?: string,
  collapsable?: boolean,
  title: string,
|};

export const Section = ({
  id,
  badges = [],
  className,
  children,
  collapsable = true,
  numbered = true,
  optional = false,
  title,
}: SectionProps) => {
  const href = `#${id}`;
  const [expanded, setExpanded] = useExpandableState(!optional, href);
  const {location, replaceLocation} = useLocation();

  const handleTitleClicked = (event: SyntheticMouseEvent<>) => {
    // If the user is holding a modifier key, or pressed a button other than the
    // left mouse button, let the browser handler it.
    if (event.metaKey || event.ctrlKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const setLocationHash = (hash) => {
      // Update the URL's hash without causing the content frame to scroll.
      replaceLocation({
        hash,
        state: {scroll: false},
        query: location.query,
      });
    };

    if (!collapsable && !optional) {
      setLocationHash(href);
    } else {
      setExpanded((wasExpanded) => {
        const isNowExpanded = !wasExpanded;
        setLocationHash(isNowExpanded ? href : '');
        return isNowExpanded;
      });
    }
  };

  const sectionClasses = classNames('Section', className, {
    'Section--numbered': numbered && !optional,
  });

  return (
    <Box className={sectionClasses} aria-expanded={expanded}>
      <InlineLink id={id} href={href} onClick={handleTitleClicked}>
        <Box padding={{top: 16, bottom: 12}}>
          <Box
            flex={{
              direction: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Heading level={2} className="Section-header">
              {optional && (
                <Text className="Section--optional">
                  <FormattedMessage {...messages.optional} />
                </Text>
              )}
              {title}
              {badges && <SectionBadgeList badges={badges} />}
            </Heading>
            {(collapsable || optional) && (
              <SectionExpandIcon expanded={expanded} />
            )}
          </Box>
        </Box>
      </InlineLink>
      {expanded && (
        <Box
          className="Section-content"
          padding={{top: 0, bottom: 32}}
          tabIndex={-1}
        >
          {children}
        </Box>
      )}
    </Box>
  );
};
